/**
 * Minimal condition evaluator for: prop <op> value
 * Supported ops: ==, !=, >=, <=, >, <
 * Special RHS: currentYear (+|-) N (parens optional)
 * Membership: prop in value1,value2,... (true when prop equals any listed value)
 * Examples it supports:
 *  - categoryLv1Id == 1ed47d2d-cc2e-49af-bfc5-fe2d13d06c08
 *  - categoryLv1Id in 731e8af2-a2ea-4a18-b195-93b7645276bf,9c9b6ef5-153f-4517-ac6e-fb98c1364ff0
 *  - fuel != 5
 *  - warranty == true
 *  - warranty == false
 *  - brandNew != true
 *  - canExchange == 1
 *  - makeYear >= currentYear - 1
 */

export interface FormData {
    [key: string]: any;
}

export class ConditionEvaluator {
    static evaluateCondition(condition: string, formData: FormData): boolean {
        if (!condition || !condition.trim()) return true;

        // "prop in a,b": true when the value, or any value of a list (search filters keep
        // categoryLv1Ids as a list), equals one of the listed values
        var membership = this.parseMembership(condition);
        if (membership) {
            var allowed = membership.values.map(value => this.parseRightValue(value));
            return this.leftValues(membership.lhs, formData).some(member =>
                allowed.some(value => this.compare(member, value, "=="))
            );
        }

        var parsed = this.parse(condition);
        if (!parsed) return false;

        var lhs = parsed.lhs;
        var op = parsed.op;
        var rhsRaw = parsed.rhsRaw;

        var left = this.leftValue(lhs, formData);

        // RHS literal
        var right = this.parseRightValue(rhsRaw);
        
        return this.compare(left, right, op);
    }

    // Every value of the field (or its plural form) as a list, for membership checks
    static leftValues(lhs: string, formData: FormData): (string | number | boolean | undefined)[] {
        var rawValue = formData[lhs];
        if (rawValue == null) {
            rawValue = formData[lhs + "s"];
        }
        var rawValues = Array.isArray(rawValue) ? rawValue : [rawValue];
        return rawValues.map(value => this.extractLeftValue(value, lhs));
    }

    // LHS from form data
    // If exact field not found, try plural form (e.g. categoryLv1Id → categoryLv1Ids)
    static leftValue(lhs: string, formData: FormData): string | number | boolean | undefined {
        var rawValue = formData[lhs];
        if (rawValue == null) {
            var pluralKey = lhs + "s";
            var pluralVal = formData[pluralKey];
            if (Array.isArray(pluralVal) && pluralVal.length > 0) {
                rawValue = pluralVal[0];
            }
        }
        return this.extractLeftValue(rawValue, lhs);
    }

    // ------ parsing ------

    static parseMembership(cond: string): { lhs: string; values: string[] } | null {
        var m = cond.match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s+in\s+(.+?)\s*$/i);
        if (!m) return null;
        var values = m[2].split(",").map(value => value.trim()).filter(value => value.length > 0);
        return values.length > 0 ? { lhs: m[1], values: values } : null;
    }

    static parse(cond: string): { lhs: string; op: string; rhsRaw: string } | null {
        // LHS identifier, operator, RHS (rest)
        var m = cond.match(/^\s*([A-Za-z_][A-Za-z0-9_]*)\s*(==|!=|>=|<=|>|<)\s*(.+?)\s*$/);
        if (!m) return null;
        return { lhs: m[1], op: m[2], rhsRaw: m[3] };
    }

    // ------ value extraction / normalization ------

    static extractLeftValue(value: any, name: string): string | number | boolean | undefined {
        if (value == null) {
            // For boolean-like field names, treat null/undefined as false
            if (this.isBooleanField(name)) return false;
            return undefined;
        }

        // Date → year
        if (value instanceof Date) return value.getFullYear();

        if (typeof value === "string") {
            // ISO-like date → year
            if (/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) {
                var d = new Date(value);
                if (!isNaN(d.getTime())) return d.getFullYear();
            }
            // numeric-looking string → number
            if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value);
            // keep as string (e.g., GUID)
            return value;
        }

        if (typeof value === "number" || typeof value === "boolean") return value;

        // Fallback
        return String(value);
    }

    static parseRightValue(raw: string): string | number | boolean | undefined {
        var t = raw.trim();

        // booleans
        if (/^(true|false)$/i.test(t)) return t.toLowerCase() === "true";

        // currentYear +/- n  (parens optional)
        var cy = t.match(/^\(?\s*currentYear\s*(?:([+-])\s*(\d+))?\s*\)?$/i);
        if (cy) {
            var base = new Date().getFullYear();
            var sign = cy[1];
            var n = cy[2] ? parseInt(cy[2], 10) : 0;
            if (sign === "-") return base - n;
            if (sign === "+") return base + n;
            return base;
        }

        // number
        if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);

        // GUID (case-insensitive)
        if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(t)) {
            return t.toLowerCase();
        }

        // "quoted string"
        var q = t.match(/^"(.*)"$/);
        if (q) return q[1];

        // Unquoted literal string
        return t;
    }

    // ------ comparison ------

    static compare(left: string | number | boolean | undefined, right: string | number | boolean, op: string): boolean {
        function isGuidStr(x: any): boolean {
            return (
                typeof x === "string" &&
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(x)
            );
        }

        // Handle undefined values for equality comparisons
        if (op === "==") {
            if (left === undefined || right === undefined) {
                return left === right;
            }
            // Normalize GUIDs to lowercase for case-insensitive equality
            if (isGuidStr(left)) left = String(left).toLowerCase();
            if (isGuidStr(right)) right = String(right).toLowerCase();
            
            var bothNum = typeof left === "number" && typeof right === "number";
            return bothNum ? Number(left) === Number(right) : String(left) === String(right);
        }
        if (op === "!=") {
            if (left === undefined || right === undefined) {
                return left !== right;
            }
            // Normalize GUIDs to lowercase for case-insensitive equality
            if (isGuidStr(left)) left = String(left).toLowerCase();
            if (isGuidStr(right)) right = String(right).toLowerCase();
            
            var bothNum = typeof left === "number" && typeof right === "number";
            return bothNum ? Number(left) !== Number(right) : String(left) !== String(right);
        }

        // For ordering operations, undefined values should not proceed
        if (left === undefined) return false;
        
        // Normalize GUIDs to lowercase for case-insensitive equality
        if (isGuidStr(left)) left = String(left).toLowerCase();
        if (isGuidStr(right)) right = String(right).toLowerCase();

        var bothNum = typeof left === "number" && typeof right === "number";

        if (bothNum) {
            var ln = Number(left);
            var rn = Number(right);
            if (op === ">") return ln > rn;
            if (op === "<") return ln < rn;
            if (op === ">=") return ln >= rn;
            if (op === "<=") return ln <= rn;
        }

        return false;
    }

    // ------ helper methods ------

    static isBooleanField(name: string): boolean {
        // Common patterns for boolean field names
        return /^(is|has|can|should|enable|disable|allow|show|hide|no)[A-Z]/.test(name) ||
               /^(checked|selected|active|visible|required|optional|valid|invalid)$/.test(name);
    }
}


// WEBPACK FOOTER //
// ./src/utils/conditionEvaluator.ts